import {
  JobMaterial,
  JobStatus,
  ProCustomer,
  ProJob,
  TimeEntry,
} from '../types/pro'
import { newLineId, toISODate } from './pro'

/**
 * One-time translation from the old day-to-day system into the Pro data model.
 *
 * The old system stored an address as a single line and a task's hours as a
 * single number, so both are unpacked here rather than lost. The old shapes
 * are declared here because they no longer exist anywhere else in the app.
 */

export interface LegacyCustomer {
  id?: string
  customerId: string
  name: string
  address: string
  phone: string
  email?: string
  notes: string
  secondaryContactName?: string
  secondaryContactPhone?: string
}

export interface LegacyTask {
  id?: string
  taskId: string
  title: string
  customerId: string
  customerName?: string
  description: string
  status: 'todo' | 'in-progress' | 'done' | 'planned' | 'pending'
  chargerType?: string
  agreedPlanning?: string
  agreedInstallation?: string
  carDeliveryDate?: string
  hours?: number
  hourlyRate?: number
  materialPrice?: number
  quotePrice?: number
  invoiced?: boolean
  subTasks?: string
  materials?: string[]
  workLog?: { date: string; description: string; hours?: number }[]
  attachments?: { name: string; uploadedAt: string }[]
}

/** "Skovvej 14, 8500 Grenaa" -> street, postal code, city. */
export function splitAddress(value: string): {
  address: string
  postalCode: string
  city: string
} {
  const raw = (value ?? '').trim()
  if (!raw) return { address: '', postalCode: '', city: '' }

  // Postal code is always four digits followed by the town.
  const match = raw.match(/(\d{4})\s+([^,]+)\s*$/)
  if (!match) return { address: raw, postalCode: '', city: '' }

  const address = raw
    .slice(0, match.index)
    .replace(/[,\s]+$/, '')
    .trim()
  return { address, postalCode: match[1], city: match[2].trim() }
}

export function customerToPro(
  customer: LegacyCustomer,
): Omit<ProCustomer, 'id'> {
  const { address, postalCode, city } = splitAddress(customer.address)
  const secondary = [
    customer.secondaryContactName,
    customer.secondaryContactPhone,
  ]
    .filter(Boolean)
    .join(' · ')

  return {
    customerNo: customer.customerId || '',
    // The old system had no customer type; a named second contact is the
    // only reliable signal that the record is a company.
    type: customer.secondaryContactName ? 'firma' : 'privat',
    name: customer.name,
    contactPerson: customer.secondaryContactName || undefined,
    address,
    postalCode,
    city,
    phone: customer.phone || '',
    email: customer.email || '',
    notes: [customer.notes, secondary && `Anden kontakt: ${secondary}`]
      .filter(Boolean)
      .join('\n\n'),
    createdDate: toISODate(new Date()),
  }
}

const STATUS_MAP: Record<LegacyTask['status'], JobStatus> = {
  todo: 'ikke-planlagt',
  planned: 'planlagt',
  'in-progress': 'i-gang',
  pending: 'afventer',
  done: 'faerdig',
}

/** Old tasks carried the planned day only; jobs need a datetime to sit in the calendar. */
function toStart(day?: string): string | undefined {
  if (!day) return undefined
  const date = new Date(day)
  if (Number.isNaN(date.getTime())) return undefined
  if (date.getHours() === 0 && date.getMinutes() === 0) date.setHours(8, 0, 0, 0)
  return date.toISOString()
}

export function taskToJob(
  task: LegacyTask,
  customerId: string,
  customerAddress: { address: string; city: string },
): Omit<ProJob, 'id'> {
  // A finished task that was already invoiced belongs one step further along.
  const status: JobStatus =
    task.status === 'done' && task.invoiced
      ? 'faktureret'
      : STATUS_MAP[task.status] ?? 'ikke-planlagt'

  const materials: JobMaterial[] = (task.materials ?? [])
    .filter(Boolean)
    .map((name) => ({
      id: newLineId(),
      name,
      itemNo: '—',
      quantity: 1,
      costPrice: 0,
      salesPrice: 0,
    }))

  // The old model kept one lump material price; keep it as a single line so
  // the money still adds up instead of silently dropping to zero.
  if (task.materialPrice && task.materialPrice > 0) {
    materials.push({
      id: newLineId(),
      name: 'Materialer (samlet fra tidligere system)',
      itemNo: '—',
      quantity: 1,
      costPrice: 0,
      salesPrice: task.materialPrice,
    })
  }

  const details = [
    task.description,
    task.subTasks && `Delopgaver:\n${task.subTasks}`,
    task.chargerType && `Ladeboks: ${task.chargerType}`,
    task.carDeliveryDate && `Bil leveres: ${task.carDeliveryDate}`,
    task.quotePrice && `Tilbudspris: ${task.quotePrice} kr`,
  ]
    .filter(Boolean)
    .join('\n\n')

  return {
    jobNo: task.taskId || '',
    customerId,
    customerName: task.customerName || '',
    address: customerAddress.address,
    city: customerAddress.city,
    title: task.title,
    description: details,
    status,
    start: toStart(task.agreedInstallation || task.agreedPlanning),
    durationHours: Math.min(Math.max(task.hours || 2, 1), 8),
    employeeIds: [],
    estimatedHours: task.hours || 0,
    // The old system priced each task on its own; keep that rate.
    hourlyRate: task.hourlyRate && task.hourlyRate > 0 ? task.hourlyRate : undefined,
    materials,
    photos: [],
    documents: (task.attachments ?? []).map((file) => ({
      id: newLineId(),
      name: file.name,
      addedAt: file.uploadedAt,
    })),
    notes: '',
  }
}

/** Work-log lines become real time entries so hours survive the move. */
export function workLogToTimeEntries(
  task: LegacyTask,
  jobId: string,
  employeeId: string,
): Omit<TimeEntry, 'id'>[] {
  const log = (task.workLog ?? []).filter((entry) => (entry.hours ?? 0) > 0)

  if (log.length > 0) {
    return log.map((entry) => ({
      employeeId,
      jobId,
      workType: 'Montage' as const,
      date: entry.date || toISODate(new Date()),
      hours: entry.hours ?? 0,
      note: entry.description || '',
      billable: true,
    }))
  }

  // No log, but the task recorded a total — keep it as one entry.
  if (task.hours && task.hours > 0) {
    return [
      {
        employeeId,
        jobId,
        workType: 'Montage',
        date: task.agreedInstallation || toISODate(new Date()),
        hours: task.hours,
        note: 'Overført fra tidligere system',
        billable: true,
      },
    ]
  }

  return []
}
